import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError, {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import {
  actions as siteActions,
  operations as siteOperations,
  selectors as siteSelectors,
} from 'store/siteManager'
import ImplementationApproachModal from './component'
import { ENTRY_ID, fieldNames, validationSchema } from './data'

export const ImplementationApproachModalContainer = ({
  clearError,
  flowLabel,
  handleClose,
  hasApiError,
  modals,
  sitePreferences,
  site,
  updateSitePreferences,
}) => {
  if (!modals) return null

  const closeModal = () => {
    clearError('sitePreferences')
    handleClose()
  }

  const getInitialValues = preferences => {
    const initialValues = {
      programIsEnabled: {
        k5Digital: preferences.k5Digital.programIsEnabled || false,
        mspDigital: preferences.mspDigital.programIsEnabled || false,
      },
    }

    fieldNames.forEach(field => {
      initialValues[field] = preferences[field] || null
    })

    return initialValues
  }

  const handleSave = async values => {
    const newSitePreferences = {
      k5Digital: {
        programIsEnabled: values.programIsEnabled.k5Digital,
      },
      mspDigital: {
        programIsEnabled: values.programIsEnabled.mspDigital,
      },
    }

    fieldNames.forEach(field => {
      newSitePreferences.k5Digital[field] = values[field]
      newSitePreferences.mspDigital[field] = values[field]
    })

    await updateSitePreferences(site.id, newSitePreferences)
  }

  const modalContent = modals[0]

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(sitePreferences)}
      validationSchema={validationSchema}
    >
      {({
        errors,
        isSubmitting,
        setFieldTouched,
        setFieldValue,
        setSubmitting,
        setTouched,
        touched,
        values,
      }) => (
        <ImplementationApproachModal
          choiceContent={modalContent.choiceContent}
          errors={errors}
          flowLabel={flowLabel}
          handleChange={(name, value) => {
            setFieldTouched(name)
            setFieldValue(name, value)
          }}
          handleClose={handleClose}
          handleNextButtonClick={async () => {
            const isValidSync = validationSchema.isValidSync(values)
            if (!isValidSync) {
              const touchedFields = fieldNames.reduce((acc, name) => {
                acc[name] = true
                return acc
              }, {})
              touchedFields.programIsEnabled = {
                k5Digital: true,
                mspDigital: true,
              }
              await setTouched(touchedFields)
              return
            }
            setSubmitting(true)
            await handleSave(values)
            setSubmitting(false)
            closeModal()
          }}
          hasApiError={hasApiError}
          header={modalContent.header}
          helpCopy={modalContent.helpCopy}
          isSubmitting={isSubmitting}
          modals={modals}
          nextLabel={modalContent.nextLabel}
          setSubmitting={setSubmitting}
          touched={touched}
          values={values}
        />
      )}
    </Formik>
  )
}

ImplementationApproachModalContainer.propTypes = {
  clearError: PropTypes.func.isRequired,
  flowLabel: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  hasApiError: PropTypes.bool.isRequired,
  modals: PropTypes.array.isRequired,
  site: PropTypes.object.isRequired,
  sitePreferences: PropTypes.object.isRequired,
  updateSitePreferences: PropTypes.func.isRequired,
}

const mapStateToProps = (state, ownProps) => {
  const site = siteSelectors.selectSiteById(state, ownProps.siteId)
  const { sitePreferences = {} } = site || {}
  const { sitePreferences: sitePreferencesAPIResults } = state.siteManager
  const { error } = sitePreferencesAPIResults
  return {
    hasApiError: !!error,
    site,
    sitePreferences: {
      ...sitePreferences?.k5Digital,
      k5Digital: {
        programIsEnabled: sitePreferences?.k5Digital?.programIsEnabled,
      },
      mspDigital: {
        programIsEnabled: sitePreferences?.mspDigital?.programIsEnabled,
      },
    },
  }
}

const mapDispatchToProps = {
  clearError: siteActions.clearError,
  updateSitePreferences: siteOperations.updateSitePreferences,
}

const mapper = entry => {
  const { flowLabel, modals } = entry || {}
  return {
    flowLabel,
    modals,
  }
}

const options = {
  entryId: ENTRY_ID,
  include: 3,
  mapper,
  spread: true,
}

export default ContentEntryDataGetter(
  withApiError(
    ImplementationApproachModalContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
